import { useEffect, useRef } from 'react'

/*
 * Background-fetches remaining notification pages after mount.
 * KnockProvider loads only 50 items initially — this hook drives pagination
 * so category counts reflect the full dataset. Safe to mount in both
 * InboxPage and InboxDonePage simultaneously (shared Knock store).
 *
 * A time-based circuit breaker stops fetching after 5 minutes to guard
 * against a stuck hasMore flag in the Knock SDK.
 */
const CIRCUIT_BREAKER_MS = 5 * 60 * 1000

export function useInboxBackgroundFetch({
  hasMore,
  isLoadingMore,
  fetchNextPage,
  delayMs = 300,
}: {
  hasMore: boolean
  isLoadingMore: boolean
  fetchNextPage: () => void
  delayMs?: number
}): void {
  const fetchRef = useRef(fetchNextPage)
  fetchRef.current = fetchNextPage

  // Refs prevent stale closures when multiple instances are mounted.
  const hasMoreRef = useRef(hasMore)
  hasMoreRef.current = hasMore
  const isLoadingMoreRef = useRef(isLoadingMore)
  isLoadingMoreRef.current = isLoadingMore

  const startTimeRef = useRef(Date.now())

  useEffect(() => {
    if (!hasMore || isLoadingMore) {
      return
    }
    if (Date.now() - startTimeRef.current > CIRCUIT_BREAKER_MS) {
      return
    }

    const timer = setTimeout(() => {
      if (!hasMoreRef.current || isLoadingMoreRef.current) {
        return
      }
      fetchRef.current()
    }, delayMs)

    return () => clearTimeout(timer)
  }, [hasMore, isLoadingMore, delayMs])
}
